import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  deleteGroupMembers,
  deleteGroupMembersUserPortal,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { DirectoryGroup, DirectoryGroupMember } from '../../../../../types';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { cloudidentity_v1 } from '@googleapis/cloudidentity';
import { useUserSettings } from '../../../../utils/use-user-settings';
import SharedDialog from '../../../../shared/shared-dialog';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteGroupMembersDialogProps {
  group: DirectoryGroup;
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  groupEmail: string;
  memberList: DirectoryGroupMember[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
  userView: boolean;
  isCloudIdentityEnabled?: boolean;
  cloudIdentityGroupMembers?: cloudidentity_v1.Schema$Membership[];
}

export default function DeleteGroupMembersDialog({
  open,
  handleClose,
  setSelectedRows,
  selectedRows,
  groupEmail,
  group,
  memberList,
  changeLogRoles,
  userView,
  isCloudIdentityEnabled,
  cloudIdentityGroupMembers,
}: DeleteGroupMembersDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { customerId } = useUserSettings();

  const selectedMembers =
    isCloudIdentityEnabled && cloudIdentityGroupMembers
      ? cloudIdentityGroupMembers.filter((member) =>
          selectedRows.includes(member.name ?? '')
        )
      : memberList.filter((member) => selectedRows.includes(member.email));

  const singleSelectedCIMember = isCloudIdentityEnabled
    ? (selectedMembers[0] as cloudidentity_v1.Schema$Membership | undefined)
    : undefined;

  const isSingleDomainWideMemberSelected =
    selectedRows.length === 1 &&
    (selectedRows[0] === '*' ||
      (isCloudIdentityEnabled &&
        (!singleSelectedCIMember?.preferredMemberKey?.id ||
          singleSelectedCIMember?.preferredMemberKey?.id === customerId)));

  const singleSelectedMemberLabel = isCloudIdentityEnabled
    ? singleSelectedCIMember?.preferredMemberKey?.id
    : selectedRows[0];

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      const userEmails =
        isCloudIdentityEnabled && cloudIdentityGroupMembers
          ? (selectedMembers as cloudidentity_v1.Schema$Membership[]).map(
              // The domain-wide "all current and future users" membership
              // doesn't reliably resolve a preferredMemberKey from the Cloud
              // Identity API - fall back to customerId, which is how it was
              // created via the classic Directory API (type=CUSTOMER).
              (member) => member.preferredMemberKey?.id || customerId || ''
            )
          : (selectedMembers as DirectoryGroupMember[]).map((member) => {
              if (member.email === '*') {
                return member.id!;
              }
              return member.email;
            });

      return deleteGroupMembers(
        groupEmail,
        'User',
        userEmails,
        changeLogMessage
      );
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: selectedRows.length > 25 ? 'info' : 'success',
        message:
          selectedRows.length > 25
            ? 'The task to remove the group members has begun.'
            : 'Group members removed successfully!',
      });

      if (selectedRows.length <= 25) {
        if (isCloudIdentityEnabled && cloudIdentityGroupMembers) {
          queryClient.setQueryData<
            cloudidentity_v1.Schema$Membership[] | undefined
          >([UserKeys.GroupMembersCloudIdentity, groupEmail], (oldData) => {
            if (!oldData) {
              return oldData;
            }
            const newMemberships = oldData.filter(
              (member) => !selectedRows.includes(member.name ?? '')
            );
            return newMemberships;
          });
        } else {
          queryClient.setQueryData(
            [UserKeys.GroupMembers, groupEmail],
            (oldData: DirectoryGroupMember[]) => {
              return oldData.filter(
                (member) => !selectedRows.includes(member.email)
              );
            }
          );
        }

        const updatedGroup = {
          ...group,
          directMembersCount:
            (group.directMembersCount ?? 0) - selectedRows.length,
        };
        queryClient.setQueryData(
          [UserKeys.DirectoryGroup, groupEmail],
          updatedGroup
        );
      }

      handleClose();
      setSelectedRows([]);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue removing the selected members from the group. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.group_details.delete_group_members, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: () => {
        const userEmails = (selectedMembers as DirectoryGroupMember[]).map(
          (member) => {
            if (member.email === '*') {
              return member.id!;
            } else {
              return member.email;
            }
          }
        );

        return deleteGroupMembersUserPortal(groupEmail, 'User', userEmails);
      },
      onSuccess: () => {
        setSnackbar({
          open: true,
          severity: selectedRows.length > 25 ? 'info' : 'success',
          message:
            selectedRows.length > 25
              ? 'The task to remove the group members has begun.'
              : 'Group members removed successfully!',
        });

        if (selectedRows.length <= 25) {
          queryClient.setQueryData(
            [UserKeys.GroupMembers, groupEmail],
            (oldData: DirectoryGroupMember[]) => {
              return oldData.filter(
                (member) => !selectedRows.includes(member.email)
              );
            }
          );

          const updatedGroup = {
            ...group,
          };
          queryClient.setQueryData(
            [UserKeys.DirectoryGroup, groupEmail],
            updatedGroup
          );
        }

        handleClose();
        setSelectedRows([]);
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue removing the selected members from the group. Please refresh and try again.',
        });
      },
      onSettled: () => {
        createEvent(analytics.userView.group_details.delete_group_members, {
          module: ModuleList.modules.userView.name,
          section: ModuleList.modules.userView.sections.groupDetails,
        });
      },
    });

  function handleSubmit() {
    if (
      !userView &&
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.RemoveMemberFromGroup.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      if (userView) {
        userPortalMutate();
      } else {
        mutate();
      }
    }
  }

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title="Remove from Group"
        dialogContent={
          <Box>
            <Typography>
              {selectedRows.length > 1 ? (
                <>
                  Remove <strong>{selectedMembers.length}</strong> members from{' '}
                  <strong>{group.email}</strong>.
                </>
              ) : isSingleDomainWideMemberSelected ? (
                <>
                  Remove{' '}
                  <strong>"All current and future users within domain"</strong>{' '}
                  from <strong>{group.email}</strong>.
                </>
              ) : (
                <>
                  Remove <strong>{singleSelectedMemberLabel}</strong> from{' '}
                  <strong>{group.email}</strong>.
                </>
              )}
            </Typography>
          </Box>
        }
        primaryButton={{
          title: selectedRows.length > 1 ? 'Remove Members' : 'Remove Member',
          color: 'warning',
          action: handleSubmit,
          loading: isPending || userPortalPending,
          disabled: isPending || userPortalPending,
        }}
      />
      {openChangeLogDialog && (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={mutate}
          mutateLoading={isPending}
        />
      )}
    </>
  );
}
